import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { MdLoyalty } from 'react-icons/md';
import { fetchPointsSettings, savePointsSettings } from '../../../services/Http/Api';
import StyledCard from '../../../shared/layout/cards/StyledCard';
import MainButton from '../../../shared/layout/buttons/MainButton';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';

const PointsSettings = () => {
    const { t } = useTranslation();
    const { register, handleSubmit, reset, formState: { errors } } = useForm();
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeSection, setActiveSection] = useState('loyalty');

    useEffect(() => {
        const loadSettings = async () => {
            try {
                const data = await fetchPointsSettings();
                setSettings(data);
                reset(data || {});
                setLoading(false);
            } catch (err) {
                toast.error(t('Settings.Points.error') + ': ' + err.message);
                setLoading(false);
            }
        };
        loadSettings();
    }, [t, reset]);

    const onSubmit = async (data) => {
        try {
            const payload = {
                id: data.id,
                organization_id: data.organization_id,
                points_per_currency_unit: parseFloat(data.points_per_currency_unit) || 0,
                points_per_cross_sell_unit: parseFloat(data.points_per_cross_sell_unit) || 0,
                max_points_per_transaction: parseFloat(data.max_points_per_transaction) || 0,
                redemption_rate: parseFloat(data.redemption_rate) || 0
            };
            await savePointsSettings(payload);
            const updatedSettings = await fetchPointsSettings();
            setSettings(updatedSettings);
            reset(payload);
            toast.success(t('Settings.Points.saveSuccess'));
        } catch (err) {
            toast.error(t('Settings.Points.saveError') + ': ' + err.message);
        }
    };

    if (loading) return <LoadingMessage>{t('Settings.loading')}</LoadingMessage>;

    return (
        <Container>
            <SectionLayout>
                <SectionTabs>
                    <SectionTab $active={activeSection === 'loyalty'} onClick={() => setActiveSection('loyalty')}>
                        <MdLoyalty size={18} />
                        {t('Settings.Points.loyaltyPoints')}
                    </SectionTab>
                </SectionTabs>
                <SectionContent>
                    {activeSection === 'loyalty' && (
                        <StyledCard title={t('Settings.Points.loyaltyPoints')}>
                            <Form onSubmit={handleSubmit(onSubmit)}>
                                <input type="hidden" {...register('id')} />
                                <input type="hidden" {...register('organization_id')} />
                                <Grid>
                                    <FormGroup>
                                        <Label>{t('Settings.Points.pointsPerCurrencyUnit')}</Label>
                                        <StyledInput
                                            type="number"
                                            step="0.0001"
                                            {...register('points_per_currency_unit', { min: { value: 0, message: t('Settings.Points.nonNegative') } })}
                                            placeholder={t('Settings.Points.pointsPerCurrencyUnitPlaceholder')}
                                        />
                                        {errors.points_per_currency_unit && <ErrorText>{errors.points_per_currency_unit.message}</ErrorText>}
                                    </FormGroup>
                                    <FormGroup>
                                        <Label>{t('Settings.Points.pointsPerCrossSellUnit')}</Label>
                                        <StyledInput
                                            type="number"
                                            step="0.0001"
                                            {...register('points_per_cross_sell_unit', { min: { value: 0, message: t('Settings.Points.nonNegative') } })}
                                            placeholder={t('Settings.Points.pointsPerCrossSellUnitPlaceholder')}
                                        />
                                        {errors.points_per_cross_sell_unit && <ErrorText>{errors.points_per_cross_sell_unit.message}</ErrorText>}
                                    </FormGroup>
                                    <FormGroup>
                                        <Label>{t('Settings.Points.maxPointsPerTransaction')}</Label>
                                        <StyledInput
                                            type="number"
                                            step="0.01"
                                            {...register('max_points_per_transaction', { min: { value: 0, message: t('Settings.Points.nonNegative') } })}
                                            placeholder={t('Settings.Points.maxPointsPerTransactionPlaceholder')}
                                        />
                                        {errors.max_points_per_transaction && <ErrorText>{errors.max_points_per_transaction.message}</ErrorText>}
                                    </FormGroup>
                                    <FormGroup>
                                        <Label>{t('Settings.Points.redemptionRate')}</Label>
                                        <StyledInput
                                            type="number"
                                            step="0.0001"
                                            {...register('redemption_rate', { min: { value: 0, message: t('Settings.Points.nonNegative') } })}
                                            placeholder={t('Settings.Points.redemptionRatePlaceholder')}
                                        />
                                        {errors.redemption_rate && <ErrorText>{errors.redemption_rate.message}</ErrorText>}
                                    </FormGroup>
                                </Grid>
                                <FormFooter>
                                    <MainButton type="submit">{t('Settings.Points.savePointsSettings')}</MainButton>
                                </FormFooter>
                            </Form>
                        </StyledCard>
                    )}
                </SectionContent>
            </SectionLayout>
        </Container>
    );
};

export default PointsSettings;

// Styled Components
const Container = styled.div`
    width: 100%;
`;

const SectionLayout = styled.div`
    display: grid;
    grid-template-columns: 240px 1fr;
    gap: 1.5rem;
    align-items: start;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

const SectionTabs = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const SectionTab = styled.button`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    border: 1px solid ${({ $active }) => ($active ? '#701a82' : 'transparent')};
    background: ${({ $active }) => ($active ? 'rgba(112, 26, 130, 0.05)' : 'transparent')};
    color: ${({ $active }) => ($active ? '#701a82' : '#6b7280')};
    font-weight: 500;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    text-align: left;

    &:hover {
        background: ${({ $active }) => ($active ? 'rgba(112, 26, 130, 0.05)' : '#f9fafb')};
        color: #701a82;
    }
`;

const SectionContent = styled.div`
    flex: 1;
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.25rem;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const Label = styled.label`
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
`;

const StyledInput = styled.input`
    width: 100%;
    padding: 0.625rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.875rem;
    color: #111827;
    outline: none;
    transition: all 0.2s;

    &:focus {
        border-color: #701a82;
        box-shadow: 0 0 0 3px rgba(112, 26, 130, 0.1);
    }
`;

const FormFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 0.625rem;
`;

const ErrorText = styled.span`
    color: #ef4444;
    font-size: 0.75rem;
`;
